// src/components/ViewerDashboard.jsx
import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { getFirestore, collection, getDocs, query, where, orderBy, limit } from "firebase/firestore";
import { Code, BookOpen, Star, ArrowRight, Zap, Check, MessageCircle } from "lucide-react";
import "../Styles/Viewer.css";

const ViewerDashboard = () => {
  const [freeCourses, setFreeCourses] = useState([]);
  const [popularCourses, setPopularCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [shouldRedirect, setShouldRedirect] = useState(false);

  const userRole = localStorage.getItem("userRole");
  const userEmail = localStorage.getItem("userEmail");
  const whatsappNumber = "0571919051"; // Change to your WhatsApp number

  useEffect(() => {
    // Check if user role has been updated to member
    if (userRole === "member" || userRole === "admin") {
    console.log("user is a: ", userRole);
      setShouldRedirect(true);
    }
  }, [userRole]);

  useEffect(() => {
    const fetchViewerData = async () => {
      try {
        setLoading(true);
        const db = getFirestore();
        const coursesRef = collection(db, "courses");

        // Fetch free courses
        const freeCoursesQuery = query(
          coursesRef,
          where("isFree", "==", true),
          orderBy("createdAt", "desc"),
          limit(3)
        );

        try {
          const freeCoursesSnapshot = await getDocs(freeCoursesQuery);
          const freeCoursesData = [];
          freeCoursesSnapshot.forEach((doc) => {
            freeCoursesData.push({ id: doc.id, ...doc.data() });
          });
          setFreeCourses(freeCoursesData);
        } catch (freeCourseError) {
          console.log("Error fetching free courses:", freeCourseError);
        }

        // Fetch popular courses
        const popularCoursesQuery = query(
          coursesRef,
          orderBy("viewCount", "desc"),
          limit(2)
        );

        try {
          const popularCoursesSnapshot = await getDocs(popularCoursesQuery);
          const popularCoursesData = [];
          popularCoursesSnapshot.forEach((doc) => {
            popularCoursesData.push({ id: doc.id, ...doc.data() });
          });
          setPopularCourses(popularCoursesData);
        } catch (popularCourseError) {
          console.log("Error fetching popular courses:", popularCourseError);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching viewer data:", err);
        setError("Failed to load content. Please try again later.");
        setLoading(false);
      }
    };

    fetchViewerData();
  }, [userEmail]);

  // Redirect if user role is updated
  if (shouldRedirect) {
    return <Navigate to="/dashboard" replace />;
  }

  const handlePlanSelection = (planName) => {
    const message = `Hello I've made a subscription for ${planName}`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, "_blank");
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading content...</p>
      </div>
    );
  }

  return (
    <div className="viewer-container">
      <div className="viewer-header">
        <div className="header-gradient"></div>
        <h1>Welcome to Skillforge Dev Hub</h1>
        <p>Explore our free content and upgrade to access premium features</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="upgrade-banner">
        <div className="banner-content">
          <div className="banner-icon-wrapper">
            <Zap size={32} className="banner-icon" />
          </div>
          <div className="banner-text">
            <h2>Unlock Premium Content</h2>
            <p>Get access to live sessions, project reviews, and premium tutorials</p>
          </div>
          <button 
            className="upgrade-button"
            onClick={() => document.querySelector('.plans').scrollIntoView({ behavior: 'smooth' })}
          >
            Upgrade Now
          </button>
        </div>
      </div>

      <div className="content-section">
        <div className="section-header">
          <BookOpen size={24} className="section-icon" />
          <h2>Free Courses</h2>
        </div>
        <div className="courses-grid">
          {freeCourses.length > 0 ? (
            freeCourses.map((course) => (
              <div key={course.id} className="course-card">
                <div className="course-image">
                  {course.imageUrl ? (
                    <img src={course.imageUrl} alt={course.title} />
                  ) : (
                    <div className="placeholder-image">
                      <Code size={48} />
                    </div>
                  )}
                </div>
                <div className="course-info">
                  <h3>{course.title}</h3>
                  <p>{course.description}</p>
                  <button className="start-course-button">
                    Start Learning <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <p className="no-content">No free courses available currently.</p>
          )}
        </div>
      </div>

      <div className="content-section">
        <div className="section-header">
          <Star size={24} className="section-icon" />
          <h2>Popular Courses</h2>
          <span className="premium-tag">Premium</span>
        </div>
        <div className="premium-courses-grid">
          {popularCourses.length > 0 ? (
            popularCourses.map((course) => (
              <div key={course.id} className="premium-course-card">
                <div className="locked-overlay">
                  <div className="lock-icon">🔒</div>
                  <span>Premium Content</span>
                </div>
                <div className="course-image">
                  {course.imageUrl ? (
                    <img src={course.imageUrl} alt={course.title} />
                  ) : (
                    <div className="placeholder-image">
                      <Code size={48} />
                    </div>
                  )}
                </div>
                <div className="course-info">
                  <h3>{course.title}</h3>
                  <p>{course.description}</p>
                </div>
              </div>
            ))
          ) : (
            <p className="no-content">No popular courses available currently.</p>
          )}
        </div>
      </div>

      <div className="subscription-options">
        <div className="subscription-header">
          <h2>Ready to take your skills to the next level?</h2>
          <p>Choose a plan that works best for you</p>
        </div>
        <div className="plans">
          <div className="plan-card">
            <div className="plan-header">
              <h3>Monthly Plan</h3>
            </div>
            <div className="price-section">
              <div className="price">K50<span>/month</span></div>
            </div>
            <ul className="features">
              <li><Check size={18} /> Access all premium courses</li>
              <li><Check size={18} /> Weekly live coding sessions</li>
              <li><Check size={18} /> Project feedback</li>
              <li><Check size={18} /> Private community access</li>
            </ul>
            <button 
              className="plan-button"
              onClick={() => handlePlanSelection("Monthly Plan - K50")}
            >
              <MessageCircle size={18} /> Subscribe via WhatsApp
            </button>
          </div>

          <div className="plan-card featured">
            <div className="best-value">🌟 Best Value</div>
            <div className="plan-header">
              <h3>Annual Plan</h3>
            </div>
            <div className="price-section">
              <div className="price">K500<span>/year</span></div>
              <div className="savings">💰 Save K100</div>
            </div>
            <ul className="features">
              <li><Check size={18} /> All monthly plan features</li>
              <li><Check size={18} /> Priority support</li>
              <li><Check size={18} /> Exclusive workshops</li>
              <li><Check size={18} /> Certificate of completion</li>
            </ul>
            <button 
              className="plan-button featured-button"
              onClick={() => handlePlanSelection("Annual Plan - K500")}
            >
              <MessageCircle size={18} /> Subscribe via WhatsApp
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewerDashboard;
